/**
 * Assemble ONE walkable floor as a `MapData` from a fixed building `Shell`:
 * subdivide AROUND the reserved stairwell core, door into a fully-connected
 * layout, type + furnish + secure + light. The core carries the Stairs tile at
 * the shell's aligned position, so stacked floors line up (Phase 1d). Windows are
 * placed on above-ground floors only (basements are windowless/dark).
 *
 * Headless + deterministic (rule 4): all randomness comes from the seeded rng.
 */

import { hashSeed, mulberry32 } from '../rng';
import { blankMap, fillRect, refreshDoorGlyph, setTile, type MapData, type Room, type DoorInfo } from '../map';
import { TileType } from '../tiles';
import type { BuildProfile, OperatingState } from '../establishment';
import { bspAdjacencies, cellFloor, sharedWallTiles, type Rect } from './bsp';
import { chooseDoors } from './circulation';
import { classifyRooms, roomLabel, type RoomType } from './rooms';
import { furnish, placeStations } from './props';
import {
  populateCameras, populateDoorLocks, populateFurnitureCaches, populateLights,
  populateSecurity, populateUniforms,
} from './populate';
import { coreStairTiles, pickShell, subdivideAround, type Shell } from './shell';

export interface FloorGenResult {
  map: MapData;
  /** The cells (outer rects), index-aligned with `map.rooms`. Index 0 is the core. */
  cells: Rect[];
  /** The aligned stair PAIR at the core (same (x,y) on every floor). */
  stairs: { up: { x: number; y: number }; down: { x: number; y: number } };
}

export interface FloorOpts {
  title?: string;
  floorIndex?: number;
  operating?: OperatingState;
  /** Below grade → no windows, and the arrival/spawn is the stairwell core. */
  isBasement?: boolean;
  /** The ground/entrance floor: spawn at a bottom-edge lobby, not the core. */
  isEntrance?: boolean;
  /** Rooms this floor MUST contain: service plant rooms (the building's
   *  required-service list, distributed basement-first by `building.ts`) plus
   *  any room the MISSION needs to exist for its objective to be anchorable.
   *  Seated on the smallest cells; a floor with too few simply seats what fits. */
  plantRooms?: readonly RoomType[];
}

/**
 * Generate a floor for a given shell (footprint + core + window lines). Every
 * floor of a building passes the SAME shell, so cores/windows align.
 */
export function generateFloorWithShell(
  shell: Shell,
  buildProfile: BuildProfile,
  seed: number,
  opts: FloorOpts = {},
): FloorGenResult {
  const rng = mulberry32(hashSeed(seed, opts.floorIndex ?? 0, 0xf100));
  const { w, h } = shell;

  const map = blankMap(w, h);
  map.title = opts.title ?? 'Building';
  map.floor = opts.floorIndex ?? 1;

  fillRect(map, 0, 0, w - 1, h - 1, TileType.Wall);

  // Cell 0 is the reserved stairwell core, then the corridor ring, then the
  // BSP'd strips outside it.
  const { cells, corridors, ring, roomRects } = subdivideAround(shell, rng);
  const isCorridor = new Set(corridors);
  const isRing = new Set(ring);
  for (const c of cells) {
    const f = cellFloor(c);
    fillRect(map, f.x0, f.y0, f.x1, f.y1, TileType.Floor);
  }

  // Circulation. The ring's own junctions are OPENED (an archway — a door there
  // would cut the route), so the corridor is one continuous space; then every
  // other cell gets a door, preferring a wall it shares with a corridor so rooms
  // open onto circulation rather than into each other.
  const adj = bspAdjacencies(cells);
  // Only the RING's own junctions become archways; a spur joins it by a door
  // (a fire door across a corridor is normal, and it keeps rooms readable).
  const seams = adj.filter((e) => isRing.has(e.a) && isRing.has(e.b));
  for (const e of seams) {
    for (const t of sharedWallTiles(cells[e.a]!, cells[e.b]!)) setTile(map, t.x, t.y, TileType.Floor);
  }
  const doors = chooseDoors(
    cells.length,
    adj.filter((e) => !(isRing.has(e.a) && isRing.has(e.b))),
    rng,
    {
      priority: (e) => (isCorridor.has(e.a) || isCorridor.has(e.b) ? 0 : 1),
      merged: ring.length ? [ring] : [],
    },
  );
  const doorSeen = new Set<number>();
  const doorList: DoorInfo[] = [];
  for (const d of doors) {
    const idx = d.y * w + d.x;
    if (doorSeen.has(idx)) continue;
    doorSeen.add(idx);
    setTile(map, d.x, d.y, TileType.DoorClosed);
    doorList.push({ x: d.x, y: d.y, kind: 'normal', lockId: null });
  }
  map.doors = doorList;
  // Now that every wall is painted, give each door the glyph for the wall it
  // sits in — open and closed must never share one.
  for (const d of doorList) refreshDoorGlyph(map, d.x, d.y);

  // The stair pair sits at the core centre, aligned across floors.
  const stairs = coreStairTiles(shell);
  setTile(map, stairs.up.x, stairs.up.y, TileType.StairsUp);
  setTile(map, stairs.down.x, stairs.down.y, TileType.StairsDown);

  // The entrance floor spawns at a bottom-edge lobby cell; other floors arrive at
  // the core. `entranceCell` (>0) is the strip cell nearest bottom-centre.
  let entranceCell = -1;
  if (opts.isEntrance) {
    let bestD = Infinity;
    cells.forEach((c, i) => {
      if (i === 0 || isCorridor.has(i)) return; // never the core or circulation
      const d = ((c.x0 + c.x1) / 2 - (w - 1) / 2) ** 2 + ((c.y0 + c.y1) / 2 - (h - 2)) ** 2;
      if (d < bestD) { bestD = d; entranceCell = i; }
    });
  }
  const spawnCell = opts.isEntrance && entranceCell >= 0 ? entranceCell : 0;
  if (spawnCell === 0) {
    // Arriving floors drop you beside the stairs, not on them.
    map.spawn = { x: stairs.down.x, y: stairs.down.y };
  } else {
    const sc = cells[spawnCell]!;
    map.spawn = { x: Math.round((sc.x0 + sc.x1) / 2), y: Math.round((sc.y0 + sc.y1) / 2) };
  }

  // Type cells: core → stairwell, entrance → lobby, rest by area. Register rooms.
  const forced = (i: number): RoomType | null =>
    i === 0 ? 'stairwell' : isCorridor.has(i) ? 'corridor' : i === entranceCell ? 'lobby' : null;
  const types = classifyRooms(cells, rng, forced, opts.plantRooms ?? []);
  const rooms: Room[] = roomRects.map((c, i) => ({
    id: i + 1,
    name: roomLabel(types[i]!),
    kind: types[i]!,
    x0: c.x0, y0: c.y0, x1: c.x1, y1: c.y1,
    lightsOn: true,
    // Guards don't camp the stairwell or the entrance lobby (corridors they do —
    // circulation is exactly where a patrol belongs).
    noPatrol: i === 0 || i === entranceCell,
  }));
  map.rooms = rooms;

  populateSecurity(map, cells, types, buildProfile, rng);
  furnish(map, cells, types, buildProfile.roomLuxury, rng);
  placeStations(map, cells, types, rng);
  populateUniforms(map, cells, types, buildProfile, rng);
  populateFurnitureCaches(map, buildProfile, rng);
  populateCameras(map, cells, types, buildProfile.lockCeil, rng);
  // Locks last: they read the finished door set, and their connectivity check
  // needs every prop already in place.
  populateDoorLocks(map, buildProfile, opts.operating ?? 'in-use', rng);
  populateLights(map, buildProfile, opts.operating ?? 'in-use', spawnCell + 1, rng, {
    cols: shell.windowCols, rows: shell.windowRows, enabled: !opts.isBasement,
  });

  return { map, cells, stairs };
}

/**
 * Single-floor convenience (used by tests + the 1e temp boot): pick a shell from
 * the profile and generate one entrance floor.
 */
export function generateFloor(
  buildProfile: BuildProfile,
  seed: number,
  opts: FloorOpts = {},
): FloorGenResult {
  const shell = pickShell(buildProfile, mulberry32(hashSeed(seed, 0x5)));
  return generateFloorWithShell(shell, buildProfile, seed, { isEntrance: true, ...opts });
}
